import React, { useState, useRef, useEffect } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import {
  FaBed,
  FaBath,
  FaMapMarkerAlt,
  FaExpandArrowsAlt,
  FaExpand,
  FaPlay,
  FaPause,
  FaPlayCircle,
} from "react-icons/fa";
import { PixelStreamingWrapper } from "./PixelStreamingWrapper";

const TABS = [
  { key: "gallery", label: "Galerie" },
  { key: "model3d", label: "3D Model" },
  { key: "virtual", label: "Visite Virtuelle" },
];

export default function FuturisticDetails({
  isOpen,
  apartmentData,
  onClose,
  onAskAgent,
}) {
  const [activeTab, setActiveTab] = useState("gallery");
  const [loadingModel, setLoadingModel] = useState(false);
  const [modelError, setModelError] = useState(null);
  const [isRotating, setIsRotating] = useState(true);
  const [isModelFullscreen, setModelFullscreen] = useState(false);
  const [pixelStreamActive, setPixelStreamActive] = useState(false);
  const [isPixelFullscreen, setPixelFullscreen] = useState(false);

  const modelRef = useRef();
  const controlsRef = useRef();
  const gltfSceneRef = useRef();
  const rendererRef = useRef();
  const cameraRef = useRef();
  const isRotatingRef = useRef(isRotating);
  const loadModelAttempt = useRef(0);

  useEffect(() => {
    isRotatingRef.current = isRotating;
  }, [isRotating]);

  useEffect(() => {
    let cleanupTimer = null;
    if (activeTab !== "virtual" && pixelStreamActive) {
      cleanupTimer = setTimeout(() => {
        setPixelStreamActive(false);
      }, 150);
    } else if (activeTab === "virtual" && cleanupTimer) {
      clearTimeout(cleanupTimer);
    }
    return () => {
      if (cleanupTimer) clearTimeout(cleanupTimer);
    };
  }, [activeTab, pixelStreamActive]);

  useEffect(() => {
    return () => cleanup3D();
  }, [apartmentData?.glbModelUrl, isOpen]);

  function cleanup3D() {
    if (rendererRef.current) {
      rendererRef.current.dispose();
      if (rendererRef.current.domElement.parentNode)
        rendererRef.current.domElement.parentNode.removeChild(
          rendererRef.current.domElement
        );
      rendererRef.current = null;
    }
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }
    gltfSceneRef.current = null;
  }

  const load3DModel = (attempt = 1) => {
    cleanup3D();
    if (!modelRef.current || !apartmentData?.glbModelUrl) return;

    const container = modelRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    if (width <= 0 || height <= 0) {
      const maxAttempts = 10;
      const delay = 100;
      if (attempt < maxAttempts) {
        console.warn(`Container dimensions invalid. Retrying load3DModel attempt ${attempt} in ${delay}ms.`);
        setTimeout(() => load3DModel(attempt + 1), delay);
      } else {
        setModelError("Impossible de charger le modèle 3D: dimensions du conteneur invalides.");
        setLoadingModel(false);
      }
      return;
    }

    setLoadingModel(true);
    setModelError(null);

    try {
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      cameraRef.current = camera;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setClearColor(0xf1f5f9, 1);
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      if (modelRef.current) {
        modelRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;
      } else {
        throw new Error("Model container disappeared during setup.");
      }

      scene.add(new THREE.AmbientLight(0xffffff, 0.5));
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(1, 1, 1).normalize();
      scene.add(dirLight);

      const loader = new GLTFLoader();
      loader.load(
        apartmentData.glbModelUrl,
        (gltf) => {
          scene.add(gltf.scene);
          gltfSceneRef.current = gltf.scene;
          gltf.scene.rotation.x = -Math.PI / 2;

          const box = new THREE.Box3().setFromObject(gltf.scene);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());

          const sizeThreshold = 0.0001;
          if (size.x <= sizeThreshold && size.y <= sizeThreshold && size.z <= sizeThreshold) {
            console.warn("Model has zero size—using default camera");
            camera.position.set(0, 0, 5);
            camera.lookAt(0, 0, 0);
          } else {
            const maxDim = Math.max(size.x, size.y, size.z);
            const fovRad = (camera.fov * Math.PI) / 180;
            const distance = (maxDim / 2) / Math.tan(fovRad / 2) * 1.5;
            camera.position.set(center.x, center.y + size.y * 0.5, center.z + distance);
            camera.lookAt(center);
          }

          const controls = new OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = 0.15;
          controls.target.copy(center);
          controls.update();
          controlsRef.current = controls;

          let stopped = false;
          function animate() {
            if (stopped) return;
            requestAnimationFrame(animate);

            if (gltfSceneRef.current && isRotatingRef.current) {
              gltfSceneRef.current.rotation.z += 0.005;
            }

            if (controlsRef.current && rendererRef.current && cameraRef.current) {
              controlsRef.current.update();
              rendererRef.current.render(scene, cameraRef.current);
            } else {
              console.warn("Stopping animation loop—missing Three.js references.");
              stopped = true;
            }
          }
          animate();
          setLoadingModel(false);
        },
        undefined,
        (err) => {
          console.error("Error loading GLTF:", err);
          setModelError("Impossible de charger le modèle 3D.");
          setLoadingModel(false);
        }
      );
    } catch (e) {
      console.error(e);
      setModelError("Une erreur est survenue lors de l'initialisation du modèle 3D.");
      setLoadingModel(false);
    }
  };

  useEffect(() => {
    if (activeTab === "model3d" && isOpen && apartmentData?.glbModelUrl) {
      load3DModel();
    } else {
      cleanup3D();
    }
  }, [activeTab, isOpen, apartmentData?.glbModelUrl, isModelFullscreen]);

  useEffect(() => {
    if (isModelFullscreen) {
      setTimeout(load3DModel, 100);
    }
  }, [isModelFullscreen]);

  if (!isOpen) return null;

  // ---- COMPONENT -----
  return (
    <div className="fixed top-0 left-0 w-full h-full bg-slate-900/60 z-[100] overflow-y-auto flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[90vw] mx-auto relative px-6 py-8 flex flex-col gap-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-full shadow"
        >
          X
        </button>
  
        {/* Header Block */}
        {activeTab !== "virtual" && (
          <div className="flex flex-col md:flex-row gap-8">
            {/* Left: Image & Chips */}
            <div className="w-full max-w-xs flex flex-col gap-4">
              <img
                src={apartmentData?.imageUrl}
                alt={apartmentData?.name}
                className="rounded-2xl shadow border border-slate-200 object-cover h-64 w-full"
              />
  
              <div className="flex gap-2 flex-wrap text-sm">
                <span className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                  <FaExpandArrowsAlt /> {apartmentData?.surface || apartmentData?.hs_square_footage} m²
                </span>
                <span className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                  <FaBed /> {apartmentData?.rooms || apartmentData?.hs_bedrooms}
                </span>
                <span className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                  <FaBath /> {apartmentData?.bathrooms || apartmentData?.hs_bathrooms}
                </span>
                {apartmentData?.glbModelUrl && (
                  <span className="flex items-center gap-2 bg-yellow-200 text-yellow-800 px-3 py-1 rounded-full font-semibold">
                    <FaExpand /> 3D
                  </span>
                )}
              </div>
            </div>
  
            {/* Right: Info */}
            <div className="flex-1 flex flex-col gap-4">
              <div className="flex justify-between items-start">
                <h2 className="text-2xl font-bold text-gray-800 max-w-[70%]">
                  {apartmentData?.name}
                </h2>
                <span className="bg-gradient-to-r from-yellow-400 to-yellow-200 px-5 py-2 rounded-xl font-semibold text-gray-900 shadow text-lg">
                  {apartmentData?.price ? `${Number(apartmentData.price).toLocaleString("fr-FR")} MAD` : ""}
                </span>
              </div>
  
              <p className="text-gray-600">
                <span className="font-medium text-gray-800">Description : </span>
                {apartmentData?.description || "Pas de description disponible."}
              </p>
  
              {(apartmentData?.hs_address_1 || apartmentData?.location) && (
                <div className="flex items-center gap-2 text-blue-800 font-medium">
                  <FaMapMarkerAlt />
                  <span>{apartmentData?.hs_address_1 || apartmentData?.location}</span>
                </div>
              )}
            </div>
          </div>
        )}
  
        {/* Tabs Header */}
        <div className="flex gap-3 mt-2">
          {TABS.filter(tab => {
            if (tab.key === 'gallery') return true;
            if (tab.key === 'model3d' && apartmentData?.glbModelUrl) return true;
            if (tab.key === 'virtual' && apartmentData?.pixelStreamUrl) return true;
            return false;
          }).map((tab) => (
            <button
              key={tab.key}
              className={`px-4 py-2 rounded-full font-medium transition-all duration-150 shadow-sm ${
                activeTab === tab.key
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
  
        {/* Tab Content */}
        <div className={`bg-slate-50/80 rounded-xl shadow-inner p-6 ${activeTab === 'virtual' ? 'min-h-[85vh]' : 'min-h-[350px]'}`}>
          {/* Gallery Tab */}
          {activeTab === "gallery" && (
            <div className="w-full flex flex-col items-center gap-4">
              <img
                src={apartmentData?.imageUrl}
                alt="Photo principale"
                className="w-full max-w-lg rounded-xl shadow-lg border"
              />
              <div className="text-gray-600 text-sm">Galerie de photos (plus d’images bientôt)</div>
            </div>
          )}
  
          {/* 3D Model Tab */}
          {activeTab === "model3d" && (
            <div className="relative w-full h-[350px]">
              <div
                ref={modelRef}
                className="w-full h-full rounded-xl shadow bg-slate-100"
                style={{ background: "linear-gradient(135deg,#f8fafc,#e0e7ef)" }}
              ></div>
  
              {loadingModel && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10 rounded-xl">
                  <svg className="animate-spin h-8 w-8 text-blue-700" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                  <span className="ml-4 text-lg text-gray-700">Chargement du modèle 3D...</span>
                </div>
              )}
  
              {modelError && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-50/80 z-10 rounded-xl">
                  <span className="text-red-800 font-semibold">{modelError}</span>
                </div>
              )}
  
              {!loadingModel && !modelError && (
                <button
                  onClick={() => setIsRotating((r) => !r)}
                  className="absolute top-4 left-4 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg z-20"
                  title={isRotating ? "Arrêter la rotation" : "Lancer la rotation"}
                >
                  {isRotating ? <FaPause /> : <FaPlay />}
                </button>
              )}
            </div>
          )}
  
          {/* Pixel Streaming Tab */}
          {activeTab === "virtual" && (
            <div className="w-full h-[85vh] flex items-center justify-center relative rounded-xl bg-slate-100 shadow">
              {!(pixelStreamActive && apartmentData?.pixelStreamUrl) ? (
                <button
                  className="bg-gradient-to-r from-blue-500 to-sky-400 hover:from-blue-600 hover:to-sky-500 text-white font-bold px-8 py-4 rounded-xl text-lg shadow transition flex items-center gap-2"
                  onClick={() => setPixelStreamActive(true)}
                >
                  <FaPlayCircle className="inline mr-2" size={24} />
                  Démarrer la visite interactive 3D
                </button>
              ) : (
                <>
                  <div className="w-full h-full rounded-xl overflow-hidden">
                    <PixelStreamingWrapper
                      initialSettings={{
                        AutoPlayVideo: true,
                        AutoConnect: true,
                        ss: apartmentData.pixelStreamUrl,
                        StartVideoMuted: true,
                        HoveringMouse: true,
                        WaitForStreamer: true,
                        WebRTCFPS: 30,
                        MouseInput: false,
                        FakeMouseWithTouches: true,
                      }}
                    />
                  </div>
  
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  ); // Placeholder until full UI logic and JSX is restored/fixed.
}
